import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, onMounted, ref } from "vue";
  import useVuelidate from "@vuelidate/core";
  import { required, numeric } from "@vuelidate/validators";
  import store from "@/store";
  import router from "@/router";
  import builderApi from "@/services/api/builderApi";
  import estateApi from "@/services/api/estateApi";
  import { LotInterface } from "@/types/LotInterface";
  import { getChangedFields } from "@/helpers/getChangedFields";
  import { EstateType } from "@/enums/EstateType";
  import { canEditLot } from "@/helpers/lot/adminLot";
  import SelectBuildersLotModal from "@/components/ui/modals/SelectBuildersLotModal.vue";
  import {
    BuilderInterface,
    BuilderInterfaceWithHouseDesigns,
  } from "@/types/BuilderInterface";
  import ToggleButton from "@/components/ui/ToggleButton.vue";
  import { useToast } from "vue-toastification";
  import { LotStatus } from "@/enums/LotStatus";
  import {
    formatPriceTrim,
    updatePriceInput,
  } from "@/helpers/transformation/price";

  interface Ids {
    builderId: string;
    housesIds: HouseIdAndSiteCosts[];
  }

  interface HouseIdAndSiteCosts {
    houseId: string;
    siteCosts: number;
    facadeIds?: string[];
  }
  const ESTATE_BUILDER_ID = "000000000000000000000000";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminLotUpdate',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();

  const isSelectBuildersModalOpen = ref(false);
  const buildersList = ref<BuilderInterface[]>([]);
  const isResidentialCommunity = ref(false);

  const userId = router.currentRoute.value.params.userId;
  const estateId = router.currentRoute.value.params.estateId;
  const stageId = router.currentRoute.value.params.stageId;
  const lotId = router.currentRoute.value.params.lotId;
  const lot = ref<LotInterface | null>(null);
  const isLotAvailable = ref(false);
  const form = reactive({
    title: "",
    blockWidth: null,
    blockDepth: null,
    size: 0,
    price: 0,
    builders: [],
    isBuildersEnabled: false,
    streetName: "",
  });
  const rules = computed(() => {
    return {
      title: { required, $autoDirty: true },
      blockWidth: { numeric, $autoDirty: true },
      blockDepth: { numeric, $autoDirty: true },
      size: { required, numeric, $autoDirty: true },
      price: { required, numeric, $autoDirty: true },
    };
  });
  const v$ = useVuelidate(rules, form);

  onMounted(() => {
    estateApi
      .getEstate(estateId as string)
      .then((res) => {
        isResidentialCommunity.value =
          res.data?.type === EstateType.RESIDENTIAL_COMMUNITY;
      })
      .catch((_err) => {
        /* estate type unavailable */
      });

    store.dispatch("lot/getLot", lotId).then((res) => {
      lot.value = res.data;

      if (lot.value?.status === LotStatus.AVAILABLE) {
        isLotAvailable.value = true;
      }

      if (!canEditLot(lot.value?.status as LotStatus)) {
        router.replace({
          name: "AdminLot",
          params: { userId, estateId, stageId, lotId },
        });
        return;
      }

      getBuilders(res.data.builders);
      form.title = res.data.title;
      form.blockWidth = res.data.blockWidth;
      form.blockDepth = res.data.blockDepth;
      form.size = res.data.size;
      form.price = res.data.price;
      form.isBuildersEnabled = res.data.isBuildersEnabled;
      form.builders = res.data.builders;
      form.streetName = res.data.streetName ?? "";
    });
  });

  async function updateLot() {
    v$.value.$touch();
    const isFormCorrect = await v$.value.$validate();

    if (!isFormCorrect) return;

    if (form.isBuildersEnabled && !form.builders.length) {
      toast.error(
        "Please, if the home & land package is enabled, add at least one builder."
      );
      return;
    }

    const data = getChangedFields(form, lot.value);

    if (Object.keys(data).length) {
      store.dispatch("lot/updateLot", { id: lotId, data }).then(() => {
        router.push({
          name: "AdminLot",
          params: { userId, estateId, stageId, lotId },
        });
      });
    } else {
      router.push({
        name: "AdminLot",
        params: { userId, estateId, stageId, lotId },
      });
    }
  }

  const getBuilders = async (ids: Ids[]) => {
    const [{ data }, estateRes] = await Promise.all([
      store.dispatch("builder/getAllBuilderWithHouses"),
      builderApi
        .getHousesByEstateId(estateId as string)
        .catch(() => ({ data: [] })),
    ]);

    const currentBuildersIdsOnLot = ids.map((builder) => builder.builderId);

    const builders = data.map((current: BuilderInterfaceWithHouseDesigns) => ({
      ...current,
      isSelected: currentBuildersIdsOnLot.includes(current._id),
      houses: mapHouses(ids, current),
    }));

    // Find estate-specific house designs not already covered by a real builder
    const builderHouseIds = new Set<string>(
      data.flatMap((b: BuilderInterfaceWithHouseDesigns) =>
        b.houses.map((h) => h._id)
      )
    );
    const estateOnlyHouses = (estateRes.data || []).filter(
      (h: { _id: string }) => !builderHouseIds.has(h._id)
    );

    if (estateOnlyHouses.length > 0) {
      const estateBuilderEntry = ids.find(
        (b) => b.builderId === ESTATE_BUILDER_ID
      );
      const estateHousesIdsOnLot =
        estateBuilderEntry?.housesIds.map((h) => h.houseId) ?? [];

      builders.push({
        _id: ESTATE_BUILDER_ID,
        name: "Exclusive Estate Designs",
        isSelected: currentBuildersIdsOnLot.includes(ESTATE_BUILDER_ID),
        houses: estateOnlyHouses.map((house: { _id: string }) => {
          const houseEntry = estateBuilderEntry?.housesIds.find(
            (h) => h.houseId === house._id
          );

          return {
            ...house,
            isSelected: estateHousesIdsOnLot.includes(house._id),
            selectedFacadeIds: houseEntry?.facadeIds
              ? [...houseEntry.facadeIds]
              : [],
          };
        }),
      } as unknown as BuilderInterface);
    }

    buildersList.value = builders;
  };

  const mapHouses = (ids: Ids[], current: BuilderInterfaceWithHouseDesigns) => {
    const builderEntry = ids.find(
      (builder) => builder.builderId === current._id
    );
    const houseIdsOnBuilder = builderEntry?.housesIds.map(
      (house) => house.houseId
    );

    return current.houses.map((house) => {
      const houseEntry = builderEntry?.housesIds.find(
        (h) => h.houseId === house._id
      );

      return {
        ...house,
        isSelected:
          house.builderId === current._id &&
          houseIdsOnBuilder?.includes(house._id),
        selectedFacadeIds: houseEntry?.facadeIds
          ? [...houseEntry.facadeIds]
          : [],
      };
    });
  };

const __returned__ = { toast, isSelectBuildersModalOpen, buildersList, isResidentialCommunity, userId, estateId, stageId, lotId, lot, isLotAvailable, form, rules, v$, updateLot, ESTATE_BUILDER_ID, getBuilders, mapHouses, reactive, computed, onMounted, ref, get useVuelidate() { return useVuelidate }, get required() { return required }, get numeric() { return numeric }, get store() { return store }, get router() { return router }, get builderApi() { return builderApi }, get estateApi() { return estateApi }, get LotInterface() { return LotInterface }, get getChangedFields() { return getChangedFields }, get EstateType() { return EstateType }, get canEditLot() { return canEditLot }, SelectBuildersLotModal, get BuilderInterface() { return BuilderInterface }, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, ToggleButton, get useToast() { return useToast }, get LotStatus() { return LotStatus }, get formatPriceTrim() { return formatPriceTrim }, get updatePriceInput() { return updatePriceInput } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})